import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';

import SingleOrMultiDayCalendar from '@/components/SingleOrMultiDayCalendar/SingleOrMultiDayCalendar';
import { CameraMapOptions } from '@/components/map/types';
import LocationSearchInput from '@/components/search/SearchInput';
import FilterTitle from '@/components/search/SearchModal/FiltersTitle';
import { SearchModal } from '@/components/search/SearchModal/SearchModal';
import FilterButton from '@/components/search/filters/FilterButton';
import { SearchType } from '@/components/search/search-type.enum';
import { useDateUtils } from '@/hooks/useDateUtils';
import { SimpleLocationSearchDto } from '@/lib/locations/dto/simple-location-search.dto';
import { DateRange } from '@/models/DateRange';
import calendarIcon from '@/public/icons/calendar.svg';
import placeIcon from '@/public/icons/placeIcon.svg';
import { DEFAULT_MAP_LOCATION_MR_SEARCH_ZOOM } from '@/utils/constants';
import {
  futureMonthsDisabled,
  pastDaysBeforeCutoffDisabled,
  weekEndDisabled,
} from '@/utils/date-picker-utils';

type LocationRangeModalProps = {
  isOpen: boolean;
  dateRange: DateRange | undefined;
  onOpenChange: () => void;
  onClose: () => void;
  onFiltersChange: (filters?: LocationDateRangeFilters) => void;
};

export type LocationDateRangeFilters = {
  dateRange: DateRange | undefined;
  localization:
    | {
        coords: CameraMapOptions;
        query: string;
      }
    | undefined;
};

export function LocationDateRangeModal({
  isOpen,
  dateRange,
  onOpenChange,
  onClose,
  onFiltersChange,
}: LocationRangeModalProps) {
  const t = useTranslations('search');
  const { format, isEqual } = useDateUtils();

  const [selectedDateRange, setSelectedDateRange] = useState<
    DateRange | undefined
  >(dateRange);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [localization, setLocalization] =
    useState<LocationDateRangeFilters['localization']>();
  const [, setRefresher] = useState(1);

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      setRefresher((x) => x + 1);
    });

    return () => clearTimeout(timer);
  }, [isOpen]);

  const dateRangeLabel = useMemo(() => {
    const range = selectedDateRange || dateRange;
    if (!range) return t('date');

    if (range instanceof Date) {
      return format(range, 'PP');
    }

    return `${format(range.from, 'PP')}${
      range.to && !isEqual(range.to, range.from)
        ? ` ${t('to')} ${format(range.to, 'PP')}`
        : ''
    }`;
  }, [selectedDateRange, dateRange]);

  const handleSearchInputItemClick = (
    coords: CameraMapOptions,
    itemValue: string
  ) => {
    setLocalization({ coords, query: itemValue });
  };

  const handleClickLocation = (location: SimpleLocationSearchDto) => {
    setLocalization({
      coords: {
        center: [location.geolocalization.lng, location.geolocalization.lat],
        zoom: DEFAULT_MAP_LOCATION_MR_SEARCH_ZOOM,
      },
      query: location.name,
    });
  };

  const handleClear = () => {
    setLocalization(undefined);
    setSelectedDateRange(undefined);
    onFiltersChange();
    setShowDatePicker(false);
  };

  const handleApply = () => {
    onFiltersChange({
      dateRange: selectedDateRange,
      localization,
    });
    onClose();
    setShowDatePicker(false);
  };

  return (
    <SearchModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={() => {
        onClose();
        setShowDatePicker(false);
      }}
      title={t('filter-modal.title-search')}
      onApply={handleApply}
      onClear={handleClear}
    >
      <div className="pb-5">
        <div className="mb-[30px] pt-10">
          <FilterTitle>{t('filter-modal.location')}</FilterTitle>
          <LocationSearchInput
            className="border border-grey-70 rounded-full h-[40px] text-base"
            controlClassName="px-[10px]"
            onClickResultItem={handleSearchInputItemClick}
            onClickLocation={handleClickLocation}
            placeholder={t('placeholder-input-short')}
            leftIndicator={
              <Image className="mr-2" src={placeIcon} alt="Geolocation icon" />
            }
            searchType={SearchType.Coworking}
            defaultSearchValue={localization?.query}
          />
        </div>

        <div>
          <FilterTitle>{t('filter-modal.date-range')}</FilterTitle>
          <FilterButton
            className="w-full py-[10px] !text-base"
            data-testid="calendar-range-filter-btn"
            icon={<Image src={calendarIcon} alt="calendar-icon" />}
            isActive={false}
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            {dateRangeLabel}
          </FilterButton>

          {showDatePicker && (
            <div className="mt-2 rounded-lg shadow">
              <SingleOrMultiDayCalendar
                selected={selectedDateRange || dateRange}
                defaultMonth={dateRange?.from}
                disabled={[
                  weekEndDisabled,
                  pastDaysBeforeCutoffDisabled,
                  futureMonthsDisabled,
                ]}
                onChange={(value) => {
                  setSelectedDateRange(value);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </SearchModal>
  );
}
